import { DestroyRef, inject, Injectable } from '@angular/core';

import {
  switchMap,
  catchError,
  debounceTime,
  distinctUntilChanged,
  tap,
} from 'rxjs/operators';
import { EditorView } from '@codemirror/view';
import { EditorState } from '@codemirror/state';

import { FeedService } from 'src/app/core/services/feed.service';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';

import {
  DropdownPosition,
  AutocompleteSuggestion,
} from '../components/query-editor/typeahead-dropdown/typeahead-dropdown.component';

export interface AutocompleteContext {
  searchTerm: string;
  propertyName?: string;
  isPropertyValue: boolean;
  cursorPosition: number;
  syntaxTreeContext?: any;
  // * NEW: Manual trigger tracking to bypass distinctUntilChanged
  isManualTrigger?: boolean;
  triggerTimestamp?: number;
}

import { BehaviorSubject, Observable, Subject, EMPTY } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class AutocompleteService {
  // * Injected services for backend integration
  private nomenclatureService?: NomenclatureService;
  private feedService?: FeedService;

  private destroyRef = inject(DestroyRef);

  // * Property mapping from the original QueryEditorComponent
  private propertyNameToBackendFieldMap = {
    content_media_type: 'post_content_media_type_id',
    keywords_content: 'post_keywords_content_auto',
    content_language: 'post_language_id',
    content_entities: 'post_nee_entities',
    content_topic: 'post_section_content_topic_id',
    content_type: 'post_section_content_type_id',
    source_geo_location: 'post_section_geo_location_id',
    content_sentiment: 'post_sentiment_id',
    content_platform: 'post_content_platform_id',
    source_industry: 'post_source_industry_id',
    source_publisher: 'post_source_publisher_id',
    source_rank_country: 'post_source_rank_country',
    source_rank_global: 'post_source_rank_global',
    source_sector: 'post_source_sector_id',
    source_type: 'post_source_type_id',
    source_country: 'post_section_country_id',
    user_category_id: 'post_user_category_id',
    post_object_type_id: 'post_object_type_id',
    source_labels: 'post_source_company_labels',
    source_media_channel: 'post_source_media_channel_id',
    source_media_channel_type: 'post_source_media_channel_type_id',
    source_type_category_id: 'post_source_type_category_id',
    content_hashtags: 'post_hashtags',
    content_synopsis_language: 'post_synopsis_language_id',
    author_is_verified: 'post_social_author_is_verified',
    content_sentiment_original: 'post_sentiment_original_id',
    workflow_stage_id: 'post_workflow_stage_id',
    quick_story_report_language: 'post_quick_story_report_language_id',
    translation_language: 'post_translation_language_id',
    post_face_recognition_status: 'post_face_recognition_status_id',
    post_face_detection_status: 'post_face_detection_status_id',
    section_content_provider_id: 'section_content_provider_id',
    source_labels_global: 'post_source_labels',
    post_sentiment_algorithm: 'post_sentiment_algorithm_id',
    content_wiki_entities: 'post_wiki_entities',
    html_fingerprint_cluster_type: 'post_source_html_fingerprint_cluster_type',
    post_algorithm_queue_status_id: 'post_algorithm_queue_status_id',
    post_social_possibly_sensitive: 'post_social_possibly_sensitive',
  };

  // * Cached property name completions
  private propertyNameCompletions: AutocompleteSuggestion[] = [];

  private searchTermSubject = new Subject<AutocompleteContext>();
  private suggestionsSubject = new BehaviorSubject<AutocompleteSuggestion[]>(
    [],
  );
  private visibilitySubject = new BehaviorSubject<boolean>(false);
  private selectedIndexSubject = new BehaviorSubject<number>(-1);
  private positionSubject = new BehaviorSubject<DropdownPosition>({
    top: 0,
    left: 0,
  });

  // * observables for external consumption
  public suggestions$ = this.suggestionsSubject.asObservable();
  public position$ = this.positionSubject.asObservable();
  public isVisible$ = this.visibilitySubject.asObservable();
  public selectedIndex$ = this.selectedIndexSubject.asObservable();

  // * cache for performance
  private suggestionCache = new Map<string, AutocompleteSuggestion[]>();

  private debounceMs = 300;
  // * Dropdown positioning configuration
  private dropdownConfig = {
    width: 300,
    maxHeight: 300,
    minHeight: 100,
    padding: 4,
  };

  constructor() {
    this.setupSearchPipeline();
  }

  // * Configure backend services (called by QueryEditorComponent)
  configureServices(
    nomenclatureService: NomenclatureService,
    feedService: any,
  ): void {
    this.nomenclatureService = nomenclatureService;
    this.feedService = feedService;
  }

  // * Load property name completions (migrated from QueryEditorComponent)
  async loadPropertyCompletions(objectTypeId: number): Promise<void> {
    if (!this.feedService) return;

    try {
      const response = await this.feedService
        .getFeedOperatorHints(objectTypeId)
        .toPromise();

      this.propertyNameCompletions = response.map((element: any) => ({
        label: element.name,
        type: 'keyword',
        info: element.description,
        apply: `${element.name}:`,
      }));
    } catch (error) {
      console.error('Error loading property completions:', error);
    }
  }

  // * Trigger autocomplete search
  search(context: AutocompleteContext): void {
    this.searchTermSubject.next(context);
  }

  // * Force autocomplete search (bypasses distinctUntilChanged for manual triggers)
  forceSearch(context: AutocompleteContext): void {
    // * Add manual trigger metadata to bypass distinctUntilChanged
    const forceContext: AutocompleteContext = {
      ...context,
      isManualTrigger: true,
      triggerTimestamp: Date.now(),
    };

    this.searchTermSubject.next(forceContext);
  }

  // * Show the dropdown at specified position
  showDropdown(position: DropdownPosition): void {
    this.positionSubject.next(position);
    this.visibilitySubject.next(true);
  }

  // * Hide the dropdown
  hideDropdown(): void {
    this.visibilitySubject.next(false);
    this.selectedIndexSubject.next(-1);
    this.suggestionsSubject.next([]);
  }

  // * Update selected index (for keyboard navigation)
  setSelectedIndex(index: number): void {
    this.selectedIndexSubject.next(index);
  }

  // * Get current suggestions
  getCurrentSuggestions(): AutocompleteSuggestion[] {
    return this.suggestionsSubject.value;
  }

  // * Get currently selected suggestion
  getSelectedSuggestion(): AutocompleteSuggestion | null {
    const suggestions = this.getCurrentSuggestions();
    const index = this.selectedIndexSubject.value;
    return index >= 0 && index < suggestions.length ? suggestions[index] : null;
  }

  // * Get current visibility state (for keyboard navigation)
  isVisible(): boolean {
    return this.visibilitySubject.value;
  }

  // * get actual line height from CodeMirror editor
  private getActualLineHeight(editorView: EditorView): number {
    try {
      const editorElement = editorView.dom;
      const computedStyle = window.getComputedStyle(editorElement);
      const lineHeight = parseFloat(computedStyle.lineHeight);

      // * if line-height is a number -> use it (because it might be 'normal')
      if (!isNaN(lineHeight) && lineHeight > 0) {
        return lineHeight;
      }

      // * Fallback: measure actual line height from a line element
      const lineElement = editorElement.querySelector('.cm-line');
      if (lineElement) {
        return lineElement.getBoundingClientRect().height;
      }

      // * Final fallback: reasonable default based on font size
      const fontSize = parseFloat(computedStyle.fontSize) || 14;

      return fontSize * 1.2; // typical line-height ratio
    } catch (error) {
      console.warn('Could not determine line height, using default:', error);

      return 20; // safe fallback
    }
  }

  // * Calculate dropdown position relative to viewport (for dropdown outside editor container)
  calculateDropdownPosition(
    editorView: EditorView,
    cursorPos: number,
  ): Promise<DropdownPosition> {
    // * Defer position calculation to avoid "reading layout during update" error
    return new Promise<DropdownPosition>((resolve) => {
      // ! defer until after the current update cycle
      setTimeout(() => {
        try {
          // * Get cursor coordinates from CodeMirror
          const coords = editorView.coordsAtPos(cursorPos);

          if (!coords) {
            resolve({ top: 0, left: 0 });
            return;
          }

          // * Use configurable dropdown dimensions
          const {
            width: DROPDOWN_WIDTH,
            maxHeight: DROPDOWN_MAX_HEIGHT,
            minHeight: DROPDOWN_MIN_HEIGHT,
            padding: PADDING,
          } = this.dropdownConfig;

          const actualLineHeight = this.getActualLineHeight(editorView);

          // * calculate position relative to Viewport - position just below the cursor line
          let left = coords.left + PADDING;
          let top = coords.top + actualLineHeight + PADDING;

          // * Handle right edge overflow - position dropdown to the left of cursor if needed
          const viewportWidth = window.innerWidth;
          const rightEdge = left + DROPDOWN_WIDTH;
          if (rightEdge > viewportWidth) {
            // ! Position dropdown to the left of cursor -> ensures it stays within viewport bounds
            left = Math.max(PADDING, coords.left - DROPDOWN_WIDTH + PADDING);
          }

          // * Handle bottom edge overflow - position dropdown above cursor if needed
          const viewportHeight = window.innerHeight;
          const availableSpaceBelow =
            viewportHeight - (coords.top + actualLineHeight);
          const availableSpaceAbove = coords.top;

          let maxHeight = DROPDOWN_MAX_HEIGHT;
          let positionAbove = false;

          if (
            availableSpaceBelow < DROPDOWN_MIN_HEIGHT &&
            availableSpaceAbove > availableSpaceBelow
          ) {
            // ! Not enough space below, but more space above - position above cursor
            positionAbove = true;
            top = coords.top - PADDING;
            maxHeight = Math.min(
              DROPDOWN_MAX_HEIGHT,
              availableSpaceAbove - PADDING,
            );
          } else {
            // ! Position below cursor
            maxHeight = Math.min(
              DROPDOWN_MAX_HEIGHT,
              availableSpaceBelow - PADDING,
            );
          }

          // * Ensure minimum height
          maxHeight = Math.max(DROPDOWN_MIN_HEIGHT, maxHeight);

          left = Math.max(0, Math.min(left, viewportWidth - DROPDOWN_WIDTH));

          if (positionAbove) {
            top = Math.max(PADDING, top - maxHeight);
          } else {
            top = Math.max(0, Math.min(top, viewportHeight - maxHeight));
          }

          const position = {
            top,
            left,
            maxHeight,
          };

          resolve(position);
        } catch (error) {
          console.warn('Error calculating dropdown position:', error);
          resolve({ top: 0, left: 0 });
        }
      }, 0);
    }) as any;
  }

  // * Navigate suggestions (for keyboard navigation)
  navigateUp(): void {
    const current = this.selectedIndexSubject.value;
    const suggestions = this.getCurrentSuggestions();

    if (suggestions.length === 0) {
      return;
    }

    const newIndex = current <= 0 ? suggestions.length - 1 : current - 1;
    this.setSelectedIndex(newIndex);
  }

  navigateDown(): void {
    const current = this.selectedIndexSubject.value;
    const suggestions = this.getCurrentSuggestions();

    if (suggestions.length === 0) {
      return;
    }

    const newIndex = current >= suggestions.length - 1 ? 0 : current + 1;
    this.setSelectedIndex(newIndex);
  }

  // * Accept the currently selected suggestion
  acceptCurrentSuggestion(): AutocompleteSuggestion | null {
    const suggestion = this.getSelectedSuggestion();
    if (suggestion) {
      this.hideDropdown();
    }
    return suggestion;
  }

  // * Smart text replacement logic - replaces text based on context
  acceptSuggestion(
    editorView: EditorView,
    suggestion: AutocompleteSuggestion,
    context: AutocompleteContext,
  ): void {
    const state = editorView.state;
    const cursorPos = context.cursorPosition;

    let from: number;
    let to: number;
    let insertText: string;

    if (context.isPropertyValue && context.propertyName) {
      // * Property value replacement
      from = this.findValueReplaceRange(
        state,
        cursorPos,
        context.searchTerm,
      ).from;
      to = this.findValueReplaceRange(state, cursorPos, context.searchTerm).to;

      // * Quote values the grammar cannot tokenize unquoted (whitespace, @, digits, dashes - e.g. emails)
      if (/[^\p{L}_.]/u.test(suggestion.label)) {
        insertText = `"${suggestion.label}"`;
      } else {
        insertText = suggestion.label;
      }
    } else {
      // * Property name replacement
      const range = this.findPropertyNameReplaceRange(
        state,
        cursorPos,
        context.searchTerm,
      );
      from = range.from;
      to = range.to;

      // * Add colon for property names
      if (suggestion.type === 'keyword' || suggestion.apply) {
        insertText =
          typeof suggestion.apply === 'string'
            ? suggestion.apply
            : `${suggestion.label}:`;
      } else {
        insertText = suggestion.label;
      }
    }

    // * Apply the replacement
    editorView.dispatch(
      state.update({
        changes: { from, to, insert: insertText },
        selection: { anchor: from + insertText.length },
      }),
    );

    // console.log('✅ Text replacement:', { from, to, insertText, suggestion });
  }

  // * Find the range to replace for property values
  private findValueReplaceRange(
    state: EditorState,
    cursorPos: number,
    searchTerm: string,
  ): { from: number; to: number } {
    const doc = state.doc;
    const line = doc.lineAt(cursorPos);
    const lineText = line.text;
    const cursorInLine = cursorPos - line.from;

    // * Find the start of the current value (after colon)
    let valueStart = cursorInLine;
    while (valueStart > 0 && lineText[valueStart - 1] !== ':') {
      valueStart--;
    }

    // * Skip whitespace after colon
    while (valueStart < lineText.length && /\s/.test(lineText[valueStart])) {
      valueStart++;
    }

    // * Handle quoted values
    let actualStart = valueStart;
    let actualEnd = cursorInLine;

    if (lineText[valueStart] === '"') {
      actualStart = valueStart + 1;
      // * Find closing quote
      let quoteEnd = actualStart;
      while (quoteEnd < lineText.length && lineText[quoteEnd] !== '"') {
        quoteEnd++;
      }
      actualEnd = quoteEnd;
    } else {
      // * Find end of unquoted value
      while (actualEnd < lineText.length && !/\s/.test(lineText[actualEnd])) {
        actualEnd++;
      }
    }

    return {
      from: line.from + actualStart,
      to: line.from + actualEnd,
    };
  }

  // * Find the range to replace for property names
  private findPropertyNameReplaceRange(
    state: EditorState,
    cursorPos: number,
    searchTerm: string,
  ): { from: number; to: number } {
    const doc = state.doc;
    const line = doc.lineAt(cursorPos);
    const lineText = line.text;
    const cursorInLine = cursorPos - line.from;

    // * Find the start of the current word
    let wordStart = cursorInLine;
    while (wordStart > 0 && /[\w_]/.test(lineText[wordStart - 1])) {
      wordStart--;
    }

    // * Find the end of the current word
    let wordEnd = cursorInLine;
    while (wordEnd < lineText.length && /[\w_]/.test(lineText[wordEnd])) {
      wordEnd++;
    }

    return {
      from: line.from + wordStart,
      to: line.from + wordEnd,
    };
  }

  // * Clear the suggestion cache
  clearCache(): void {
    this.suggestionCache.clear();
  }

  // * Configure dropdown positioning parameters - currently not used but might be useful in the future
  configureDropdownPositioning(
    config: Partial<typeof this.dropdownConfig>,
  ): void {
    this.dropdownConfig = { ...this.dropdownConfig, ...config };
  }

  private setupSearchPipeline(): void {
    this.searchTermSubject
      .pipe(
        debounceTime(this.debounceMs),
        distinctUntilChanged((prev, curr) => {
          // * STEP 4 FIX: Always allow manual triggers through - bypassing distinctUntilChanged
          if (curr.isManualTrigger) {
            return false; // Never consider manual triggers as duplicates
          }

          // * Original comparison for automatic triggers
          const isDuplicate =
            prev.searchTerm === curr.searchTerm &&
            prev.propertyName === curr.propertyName &&
            prev.isPropertyValue === curr.isPropertyValue;

          if (isDuplicate) {
            // console.log('🚫 Duplicate automatic search blocked:', curr);
          }

          return isDuplicate;
        }),
        switchMap((context) =>
          this.performSearch(context).pipe(
            catchError((error) => {
              console.error('Autocomplete search error:', error);
              return EMPTY;
            }),
          ),
        ),
        tap((suggestions) => {
          this.suggestionsSubject.next(suggestions);
          this.selectedIndexSubject.next(-1); // Reset selection
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private performSearch(
    context: AutocompleteContext,
  ): Observable<AutocompleteSuggestion[]> {
    return new Observable((observer) => {
      // Generate cache key
      const cacheKey = this.generateCacheKey(context);

      // Check cache first
      if (this.suggestionCache.has(cacheKey)) {
        observer.next(this.suggestionCache.get(cacheKey)!);
        observer.complete();
        return;
      }

      // Determine what type of suggestions to provide
      if (context.isPropertyValue && context.propertyName) {
        // * Property value completion (e.g., source_country:"United...")
        this.searchPropertyValues(context, cacheKey, observer);
      } else {
        // * Property name completion (e.g., "source_coun...")
        this.searchPropertyNames(context, cacheKey, observer);
      }
    });
  }

  // * Search for property value suggestions (migrated from getAutocompleteSuggestions)
  // * this is Phase 2 of the autocomplete system
  // * Phase 2
  private async searchPropertyValues(
    context: AutocompleteContext,
    cacheKey: string,
    observer: any,
  ): Promise<void> {
    if (!this.nomenclatureService) {
      observer.next([]);
      observer.complete();
      return;
    }

    try {
      // * Clean search term (remove quotes)
      let searchTerm = context.searchTerm.replace(/['"]+/g, '');

      // * Resolve backend field name
      const propertyName = context.propertyName!;
      const backendField =
        this.propertyNameToBackendFieldMap[propertyName] || propertyName;

      // * STEP 6 FIX: Send full search term to backend for proper filtering
      const trimmedTerm = searchTerm.trim();
      const lookupTerm = trimmedTerm; // Send complete search term, not just first character

      // * Fetch from backend
      const backendSuggestions = await this.nomenclatureService
        .fetchAutocompleteResults(lookupTerm, backendField)
        .toPromise();

      // * Convert to AutocompleteSuggestion format
      const suggestions: AutocompleteSuggestion[] = backendSuggestions.map(
        (element: any) => {
          const suggestion: AutocompleteSuggestion = {
            label: element.name.toString(),
            type: 'value',
            data: element,
          };

          // * Quote values the grammar cannot tokenize unquoted (whitespace, @, digits, dashes - e.g. emails)
          if (/[^\p{L}_.]/u.test(suggestion.label)) {
            suggestion.apply = (
              view: EditorView,
              completion: any,
              from: number,
              to: number,
            ) => {
              const textToInsert = `"${completion.label}"`;
              view.dispatch(
                view.state.update({
                  changes: { from, to, insert: textToInsert },
                }),
              );
            };
          }

          return suggestion;
        },
      );

      // * Cache and return results
      this.suggestionCache.set(cacheKey, suggestions);
      observer.next(suggestions);
      observer.complete();
    } catch (error) {
      observer.next([]);
      observer.complete();
    }
  }

  // * Search for property name suggestions (using fuzzy matching)
  // * this is Phase 1 of the autocomplete system
  // * Phase 1
  private searchPropertyNames(
    context: AutocompleteContext,
    cacheKey: string,
    observer: any,
  ): void {
    try {
      const searchTerm = context.searchTerm.toLowerCase();

      if (!searchTerm) {
        // * Return all property names if no search term
        this.suggestionCache.set(cacheKey, this.propertyNameCompletions);
        observer.next(this.propertyNameCompletions);
        observer.complete();
        return;
      }

      // * Fuzzy match property names (simplified version of original logic)
      const matches = this.propertyNameCompletions.filter((completion) => {
        const label = completion.label.toLowerCase();
        return label.includes(searchTerm) || label.startsWith(searchTerm);
      });

      // * Sort: prefix matches first, then contains matches
      matches.sort((a, b) => {
        const aLabel = a.label.toLowerCase();
        const bLabel = b.label.toLowerCase();
        const aStarts = aLabel.startsWith(searchTerm) ? 0 : 1;
        const bStarts = bLabel.startsWith(searchTerm) ? 0 : 1;

        if (aStarts !== bStarts) return aStarts - bStarts;
        return aLabel.localeCompare(bLabel);
      });

      // * Cache and return results
      this.suggestionCache.set(cacheKey, matches);
      observer.next(matches);
      observer.complete();

      // console.log(
      //   `✅ Property name suggestions for "${searchTerm}":`,
      //   matches.length,
      // );
    } catch (error) {
      console.error('❌ Error searching property names:', error);
      observer.next([]);
      observer.complete();
    }
  }

  private generateCacheKey(context: AutocompleteContext): string {
    // * STEP 4 FIX: Include manual trigger info in cache key to prevent cache conflicts
    const triggerType = context.isManualTrigger ? 'manual' : 'auto';
    const timestamp = context.isManualTrigger ? context.triggerTimestamp : '';
    return `${context.isPropertyValue ? 'value' : 'property'}-${context.propertyName || ''}-${context.searchTerm}-${triggerType}-${timestamp}`;
  }
}
